import React, { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";

interface LogoUploadProps {
  currentLogoUrl?: string;
  onUpload: (file: File) => Promise<void>;
  isUploading?: boolean;
}

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const LogoUpload: React.FC<LogoUploadProps> = ({
  currentLogoUrl,
  onUpload,
  isUploading = false,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setSelectedImage(reader.result as string);
        setIsModalOpen(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const createCroppedImage = async (imageSrc: string, pixelCrop: Area): Promise<Blob | null> => {
    const image = new Image();
    image.src = imageSrc;

    return new Promise((resolve, reject) => {
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        // Set canvas size to cropped area
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        // Draw the cropped image
        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height
        );

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          "image/png",
          1
        );
      };

      image.onerror = () => {
        reject(new Error("Failed to load image"));
      };
    });
  };

  const handleCropSave = async () => {
    if (!selectedImage || !croppedAreaPixels) return;

    try {
      const croppedBlob = await createCroppedImage(selectedImage, croppedAreaPixels);
      if (croppedBlob) {
        const file = new File([croppedBlob], "logo.png", { type: "image/png" });
        await onUpload(file);
        setIsModalOpen(false);
        setSelectedImage(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      console.error("Failed to crop image:", error);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        {/* Logo Preview */}
        <div className="w-24 h-24 border-2 border-border-gray bg-gray-50 flex items-center justify-center overflow-hidden">
          {currentLogoUrl ? (
            <img
              src={currentLogoUrl}
              alt="Practice logo"
              className="w-full h-full object-contain"
            />
          ) : (
            <span className="text-xs text-gray-400 text-center px-2">No logo</span>
          )}
        </div>

        {/* Upload Button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/jpg"
            onChange={handleFileSelect}
            className="hidden"
            id="logo-upload"
          />
          <label htmlFor="logo-upload">
            <Button
              type="button"
              variant="outline"
              className="px-4 h-10 border-border-gray text-black hover:bg-gray-50 text-sm cursor-pointer"
              disabled={isUploading}
              asChild
            >
              <span>{isUploading ? "Uploading..." : "Upload Logo"}</span>
            </Button>
          </label>
          <p className="text-xs text-gray-500 mt-1">PNG or JPG (recommended: 200x200px)</p>
        </div>
      </div>

      {/* Crop Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCancel} title="Crop Logo" className="max-w-3xl">
        <div className="space-y-4">
          {/* Cropper */}
          <div className="relative w-full h-96 bg-gray-900">
            {selectedImage && (
              <Cropper
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>

          {/* Zoom Control */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Zoom</label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="px-6 h-12 border-border-gray text-black hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleCropSave}
              className="px-6 h-12 bg-primary-blue hover:bg-primary-blue/90 text-white"
            >
              Save Logo
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
